import { axiosInstance } from "./index";

export interface AudienceAgeDistribution {
	ageRange: string;
	percentage: number;
}

export const CREATOR_CATEGORIES = [
	"ACTING",
	"ATHLETES AND SPORT",
	"BEAUTY + FASHION",
	"BUSINESS",
	"COMEDY",
	"COOKING",
	"CREATIVE + DIY + ARTS",
	"ENTERTAINMENT",
	"FAMILY",
	"RELATIONSHIPS",
	"HEALTH + FITNESS",
	"HOME DECOR",
	"HOSTS",
	"LGBTQIA+",
	"LIFESTYLE",
	"PETS",
	"PODCAST",
	"TRAVEL",
	"MUSIC",
] as const;

export type CreatorCategory = (typeof CREATOR_CATEGORIES)[number];

export const CREATOR_TIERS = ["Nano", "Micro", "Mid", "Macro", "Mega"] as const;

export type CreatorTier = (typeof CREATOR_TIERS)[number];

export interface CreatorInfo {
	id: string;
	name: string;
	categories: CreatorCategory[];
	tier: CreatorTier | "";
	sampleContents: string[];
	rationaleForProposing: string;
	location: string;
	userId?: string | null;
	contactEmail?: string | null;
}

export interface CreatorSocialMediaAccount {
	id: string;
	avatarUrl: string;
	creatorId: string | null;
	platform: string;
	url: string;
	platformUsername: string;
	followers: number | null;
	avgEngagementRate: number | null;
	avgViews: number | null;
	localAudiencePercentage: number | null;
	femalePercentage: number | null;
	malePercentage: number | null;
	audienceAgeDistribution: AudienceAgeDistribution[];
	lastUpdatedAt: string;
}

export interface CreatorPlatformResponse {
	creator: CreatorInfo;
	socialAccounts: CreatorSocialMediaAccount[];
}

export interface CreatorPlatformRequest {
	url: string;
	platform: string;
}

export const creatorAPI = {
	async getCreatorFromPlatform(
		campaignId: string,
		urls: CreatorPlatformRequest[],
	): Promise<CreatorPlatformResponse | null> {
		try {
			const response = await axiosInstance.post<CreatorPlatformResponse>("/creators/platform", {
				campaignId,
				urls,
			});
			return response.data;
		} catch (error) {
			console.error("Error fetching creator from platform:", error);
			throw error;
		}
	},

	async updateCreatorCategories(creatorId: string, categories: string[], campaignId: string): Promise<boolean> {
		try {
			await axiosInstance.put(`/creators/${creatorId}/categories`, {
				categories,
				campaignId,
			});
			return true;
		} catch (error) {
			console.error("Error updating creator categories:", error);
			throw error;
		}
	},

	async batchUpdateCategoryTier(
		campaignId: string,
		updates: Array<{ creatorId: string; categories?: string[]; tier?: string }>,
	): Promise<boolean> {
		try {
			await axiosInstance.put(`/campaigns/${campaignId}/creators/batch-category-tier`, { updates });
			return true;
		} catch (error) {
			console.error("Error batch updating category/tier:", error);
			throw error;
		}
	},

	async updateCreatorTier(creatorId: string, tier: string, campaignId: string): Promise<boolean> {
		try {
			await axiosInstance.put(`/creators/${creatorId}/tiers`, {
				tiers: tier,
				campaignId,
			});
			return true;
		} catch (error) {
			console.error("Error updating creator tier:", error);
			throw error;
		}
	},
};

export default creatorAPI;
